import {useState} from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Chip,
  IconButton,
  Stack,
  Collapse,
  Typography,
  InputAdornment
} from "@mui/material";
import {IconPlayerPlay, IconPrompt, IconX} from "@tabler/icons-react";
import {green, orange} from "@mui/material/colors";
import {GraphRepository} from "../../../../repositories/GraphRepository";

export const GraphSearchFilters = ({
                                     subjectOptions,
                                     predicateOptions,
                                     objectOptions,
                                     onSubjectChange,
                                     onPredicateChange,
                                     onObjectChange,
                                     colorMap
                                   }) => {
  const [tempSubject, setTempSubject] = useState(null);
  const [tempPredicate, setTempPredicate] = useState(null);
  const [tempObject, setTempObject] = useState(null);
  const [queryMode, setQueryMode] = useState(false);
  const [query, setQuery] = useState("");
  const [queryResult, setQueryResult] = useState("");

  // Determine what to show in autocomplete based on what's selected
  const getPlaceholder = () => {
    if (!tempSubject) return "Select Subject...";
    if (!tempPredicate) return "Select Predicate...";
    if (!tempObject) return "Select Object...";
    return "Search complete";
  };

  const getOptions = () => {
    if (!tempSubject) return subjectOptions;
    if (!tempPredicate) return predicateOptions;
    if (!tempObject) return objectOptions;
    return [];
  };

  const handleAutocompleteChange = (event, newValue) => {
    if (!tempSubject) {
      setTempSubject(newValue);
    } else if (!tempPredicate) {
      setTempPredicate(newValue);
    } else if (!tempObject) {
      setTempObject(newValue);
    }
  };

  const handleRemoveSubject = () => {
    setTempSubject(null);
    setTempPredicate(null);
    setTempObject(null);
  };

  const handleRemovePredicate = () => {
    setTempPredicate(null);
    setTempObject(null);
  };

  const handleRemoveObject = () => {
    setTempObject(null);
  };

  const handleApplyFilter = () => {
    onSubjectChange(tempSubject);
    onPredicateChange(tempPredicate);
    onObjectChange(tempObject);
  };

  const getOptionLabel = (option) => {
    if (typeof option === "string") return option;
    return `${option.label} (${option.type})`;
  };

  const runNeo4jQuery = () => {
    if (!query || query?.trim() === "") {
      return;
    }
    GraphRepository.executeScript(query)
      .then((response) => {
        setQueryResult(response?.data?.message?.join("\n"));
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <Box
      className={"border-shadow"}
      sx={{
        p: 1.5,
        bgcolor: "background.paper",
        borderRadius: 5,
        display: 'flex',
        flexDirection: 'column',
        // gap: 1.5,
        position: "absolute",
        top: 10,
        left: 10,
        zIndex: 100,
        minWidth: 400
      }}
    >
      <Collapse in={!queryMode}>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{flex: 1}}>
            {tempSubject && (
              <Chip
                label={tempSubject.label || tempSubject}
                onDelete={handleRemoveSubject}
                sx={{
                  bgcolor: colorMap?.get(tempSubject.type)?.background || green[300],
                  color: colorMap?.get(tempSubject.type)?.textColor || "white",
                  "& .MuiChip-deleteIcon": {
                    color: colorMap?.get(tempSubject.type)?.textColor || "white"
                  }
                }}
              />
            )}

            {tempPredicate && (
              <Chip
                label={tempPredicate}
                onDelete={handleRemovePredicate}
              />
            )}

            {tempObject && (
              <Chip
                label={tempObject.label || tempObject}
                onDelete={handleRemoveObject}
                sx={{
                  bgcolor: colorMap?.get(tempObject.type)?.background || orange[300],
                  color: colorMap?.get(tempObject.type)?.textColor || "white",
                  "& .MuiChip-deleteIcon": {
                    color: colorMap?.get(tempObject.type)?.textColor || "white"
                  }
                }}
              />
            )}

            {(!tempSubject || !tempPredicate || !tempObject) && (
              <Autocomplete
                sx={{
                  minWidth: 250,
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none"
                    }
                  }
                }}
                forcePopupIcon={false}
                popupIcon={false}
                size={"small"}
                options={getOptions()}
                getOptionLabel={getOptionLabel}
                value={null}
                blurOnSelect
                onChange={handleAutocompleteChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={getPlaceholder()}
                  />
                )}
              />
            )}
          </Stack>

          <Box>
            <IconButton
              variant={"outlined"}
              onClick={handleApplyFilter}
            >
              <IconPlayerPlay size={20}/>
            </IconButton>
            <IconButton
              variant={"outlined"}
              onClick={() => setQueryMode(!queryMode)}
            >
              <IconPrompt size={20}/>
            </IconButton>
          </Box>
        </Stack>
      </Collapse>

      <Collapse in={queryMode}>
        <Stack direction={"column"} gap={0.5}>
          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography fontWeight={500}>Execute Neo4j Query</Typography>
            <Box>
              <IconButton
                variant={"outlined"} size={"small"}
                onClick={() => setQueryMode(false)}
                sx={{alignSelf: 'flex-end'}}
              >
                <IconX size={18}/>
              </IconButton>
            </Box>
          </Stack>
          <TextField
            multiline
            minRows={2}
            maxRows={4}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Enter Neo4j query..."
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={runNeo4jQuery}
                    disabled={!query || query?.trim() === ""}
                    edge="end"
                  >
                    <IconPlayerPlay size={20}/>
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          {queryResult && (
            <Typography variant="body2">
              <strong style={{fontWeight: 500}}>Query executed:</strong> {queryResult}
            </Typography>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};